<script setup>
// Strona wyników /{alias} (makiety „Wyniki – makiety”: W1–W5, H). Nagłówek z logo, nazwą, datą i stanem
// wydarzenia, lista bez przewijania w bok na telefonie, partnerzy w stopce. Bez zakładek Międzyczasy/Statystyki.
//
// Wydajność: wiersze liczone raz na pobranie /splits (utils/results-list.js), filtry i szukanie lokalnie,
// pierwsze FIRST_ROWS wierszy od razu, reszta na raty; co sekundę odświeża się tylko czas zawodników w trasie
// (RunningTime) — wcześniej cała tabela. Pomiar i uzasadnienie: zadanie w Asanie, CLAUDE.md.
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, toRaw, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useResultsStore } from '../stores/results-fetcher.js'
import EventHeader from './results/EventHeader.vue'
import PartnersFooter from './results/PartnersFooter.vue'
import RunningTime from './results/RunningTime.vue'
import { buildResultsRows, categoryOptions, filterRows, groupRows } from '../utils/results-list.js'
import { eventPhase } from '../utils/event-info.js'
import { formatClock, parseApiTime } from '../utils/polish-time.js'

const REFRESH_MS = 30000
const FIRST_ROWS = 150 // od razu
const ROWS_PER_STEP = 400 // kolejne porcje w tle
const CHANGED_MS = 30000 // jak długo podświetlać zmieniony wiersz
const PHONE_QUERY = '(max-width: 767.98px)'

const resultsStore = useResultsStore()
const route = useRoute()
const alias = String(route.params.eventAlias || '')

const loaded = ref(false)
const lastUpdate = ref(null)
const phase = ref(null)

// Magazyn trzyma poprzednie wydarzenie, dopóki nie przyjdzie nowe — bierzemy tylko to z adresu.
const eventPayload = computed(() => {
  const payload = resultsStore.event
  const ev = payload?.event
  return ev && (!ev.alias || ev.alias === alias) ? payload : null
})
const event = computed(() => eventPayload.value?.event || null)
const sponsors = computed(() => eventPayload.value?.sponsors || [])

// ---------- wiersze (raz na pobranie) ----------
const rows = shallowRef([])
const hasPoints = ref(false)
const hasGender = ref(false)
const categories = shallowRef([])
const changedIds = shallowRef(new Set())
let previousSignatures = null
let changedTimer = null

function signature(row) {
  return `${row.group}|${row.timeText}|${row.place}|${row.penaltyMin}|${row.points}`
}

function markChanged(built) {
  const next = new Map(built.map(row => [row.id, signature(row)]))
  if (previousSignatures) {
    const changed = new Set()
    next.forEach((sig, id) => {
      if (previousSignatures.has(id) && previousSignatures.get(id) !== sig) changed.add(id)
    })
    if (changed.size) {
      changedIds.value = changed
      clearTimeout(changedTimer)
      changedTimer = setTimeout(() => { changedIds.value = new Set() }, CHANGED_MS)
    }
  }
  previousSignatures = next
}

function rebuild() {
  const list = toRaw(resultsStore.results)
  hasPoints.value = resultsStore.shouldShowPointsColumn()
  hasGender.value = resultsStore.hasGender()
  categories.value = toRaw(resultsStore.categories) || []
  const built = buildResultsRows(list, {
    categories: categories.value,
    eventStartMs: parseApiTime(event.value?.date_start),
    gapOf: runner => resultsStore.getGapToLeader(runner, hasPoints.value),
    hasPoints: hasPoints.value
  })
  markChanged(built)
  rows.value = built
  phase.value = eventPhase(event.value, Date.now())
  lastUpdate.value = Date.now()
  scheduleRender()
}

// ---------- filtry ----------
const query = ref('')
const gender = ref('all')
const category = ref('all')
const showDns = ref(false)
const sheetOpen = ref(false)

const catOptions = computed(() => categoryOptions(rows.value, categories.value))
const genderCounts = computed(() => {
  const counts = { all: rows.value.length, K: 0, M: 0 }
  rows.value.forEach(row => { if (row.gender) counts[row.gender] += 1 })
  return counts
})
const filtered = computed(() => filterRows(rows.value, { query: query.value, gender: gender.value, category: category.value }))
const groups = computed(() => groupRows(filtered.value))
const filtersActive = computed(() => gender.value !== 'all' || category.value !== 'all' || query.value.trim() !== '')
const activeFilterCount = computed(() => (gender.value !== 'all' ? 1 : 0) + (category.value !== 'all' ? 1 : 0))
const dnsVisible = computed(() => showDns.value || query.value.trim() !== '')
const categoryLabel = computed(() => catOptions.value.find(c => c.id === category.value)?.name || '')

function plural(n, one, few, many) {
  if (n === 1) return one
  const last = n % 10
  const lastTwo = n % 100
  return last >= 2 && last <= 4 && (lastTwo < 12 || lastTwo > 14) ? few : many
}

const summary = computed(() => {
  const all = rows.value.filter(r => r.group !== 'dns').length
  const finished = rows.value.filter(r => r.group === 'finished').length
  if (phase.value === 'before') return `${all} ${plural(all, 'osoba', 'osoby', 'osób')} na liście startowej`
  return `${all} ${plural(all, 'osoba', 'osoby', 'osób')} · ${finished} na mecie`
})

function clearFilters() {
  gender.value = 'all'
  category.value = 'all'
}

// ---------- lista do narysowania (na raty) ----------
const courseLabel = computed(() => (phase.value === 'before' ? 'Lista startowa' : phase.value === 'ended' ? 'Bez mety' : 'W trasie'))
const items = computed(() => {
  const g = groups.value
  const out = g.finished.map(row => ({ key: row.id, row }))
  if (g.course.length) {
    out.push({ key: 'h-course', section: `${courseLabel.value} · ${g.course.length}` })
    g.course.forEach(row => out.push({ key: row.id, row }))
  }
  if (g.dnf.length) {
    out.push({ key: 'h-dnf', section: `Nie ukończyli · ${g.dnf.length}` })
    g.dnf.forEach(row => out.push({ key: row.id, row }))
  }
  if (g.dns.length && dnsVisible.value) {
    out.push({ key: 'h-dns', section: `Nie wystartowali · ${g.dns.length}` })
    g.dns.forEach(row => out.push({ key: row.id, row }))
  }
  return out
})
const renderLimit = ref(FIRST_ROWS)
const visibleItems = computed(() => (items.value.length > renderLimit.value ? items.value.slice(0, renderLimit.value) : items.value))
let renderHandle = null

function scheduleRender() {
  if (renderHandle || renderLimit.value >= items.value.length) return
  const step = () => {
    renderHandle = null
    renderLimit.value += ROWS_PER_STEP
    scheduleRender()
  }
  renderHandle = window.requestIdleCallback ? window.requestIdleCallback(step, { timeout: 200 }) : setTimeout(step, 16)
}
watch(items, scheduleRender)

// ---------- telefon / komputer ----------
const isPhone = ref(false)
let phoneMedia = null
function onMediaChange(e) { isPhone.value = e.matches }

function onKeydown(e) {
  if (e.key === 'Escape' && sheetOpen.value) sheetOpen.value = false
}

// ---------- pobieranie ----------
let refreshTimer = null
onMounted(async () => {
  document.documentElement.classList.add('rv-page')
  phoneMedia = window.matchMedia(PHONE_QUERY)
  isPhone.value = phoneMedia.matches
  phoneMedia.addEventListener?.('change', onMediaChange)
  document.addEventListener('keydown', onKeydown)

  // najpierw wydarzenie: zawodnicy bez start_time liczą czas od startu wydarzenia
  await resultsStore.fetchEventDetails(alias)
  await resultsStore.fetchResults(alias)
  loaded.value = true
  rebuild()
  refreshTimer = setInterval(() => resultsStore.fetchResults(alias), REFRESH_MS)
})

onBeforeUnmount(() => {
  document.documentElement.classList.remove('rv-page')
  phoneMedia?.removeEventListener?.('change', onMediaChange)
  document.removeEventListener('keydown', onKeydown)
  clearInterval(refreshTimer)
  clearTimeout(changedTimer)
  if (renderHandle) (window.cancelIdleCallback || clearTimeout)(renderHandle)
})

// kolejne pobrania (co 30 s) — przelicz wiersze; pierwsze liczy onMounted po wczytaniu wydarzenia
watch(() => resultsStore.results, () => {
  if (loaded.value) rebuild()
})

const updatedAtText = computed(() => (lastUpdate.value ? formatClock(lastUpdate.value) : ''))
const nowAtBuild = computed(() => lastUpdate.value || Date.now())
</script>

<template>
  <div class="rv">
    <EventHeader :event="event" :alias="alias" :last-update="lastUpdate" />

    <main class="rv__main">
      <!-- komputer: filtry w jednym rzędzie -->
      <div v-if="!isPhone" class="rv-toolbar">
        <label class="rv-search">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
          <input v-model="query" type="search" enterkeyhint="search" placeholder="Szukaj: nazwisko albo numer" aria-label="Szukaj zawodnika po nazwisku albo numerze" />
        </label>
        <div v-if="hasGender" class="rv-seg" role="group" aria-label="Płeć">
          <button v-for="opt in [['all', 'Wszyscy'], ['K', 'Kobiety'], ['M', 'Mężczyźni']]" :key="opt[0]" type="button" :aria-pressed="gender === opt[0] ? 'true' : 'false'" @click="gender = opt[0]">{{ opt[1] }}</button>
        </div>
        <label v-if="catOptions.length > 1" class="rv-select" :class="{ 'is-active': category !== 'all' }">
          <span>Kategoria:</span>
          <select v-model="category" aria-label="Kategoria">
            <option value="all">wszystkie</option>
            <option v-for="c in catOptions" :key="c.id" :value="c.id">{{ c.name }} · {{ c.count }}</option>
          </select>
        </label>
        <span class="rv-toolbar__spacer" />
        <span class="rv-toolbar__summary">{{ summary }}</span>
      </div>

      <!-- telefon: szukaj + Filtry, aktywne filtry jako pigułki -->
      <div v-else class="rv-ptools">
        <div class="rv-ptools__row">
          <label class="rv-search rv-search--phone">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><circle cx="11" cy="11" r="7" /><path d="M20 20l-3.5-3.5" /></svg>
            <input v-model="query" type="search" enterkeyhint="search" placeholder="Szukaj: nazwisko, nr" aria-label="Szukaj zawodnika po nazwisku albo numerze" />
          </label>
          <button v-if="hasGender || catOptions.length > 1" type="button" class="rv-filters-btn" :class="{ 'is-active': activeFilterCount }" aria-haspopup="dialog" @click="sheetOpen = true">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true"><path d="M4 6h16M7 12h10M10 18h4" /></svg>
            Filtry<span v-if="activeFilterCount" class="rv-filters-btn__badge">{{ activeFilterCount }}</span>
          </button>
        </div>
        <div v-if="activeFilterCount" class="rv-chips">
          <span v-if="gender !== 'all'" class="rv-chip">{{ gender === 'K' ? 'Kobiety' : 'Mężczyźni' }}<button type="button" :aria-label="`Usuń filtr ${gender === 'K' ? 'Kobiety' : 'Mężczyźni'}`" @click="gender = 'all'">×</button></span>
          <span v-if="category !== 'all'" class="rv-chip">{{ categoryLabel }}<button type="button" :aria-label="`Usuń filtr ${categoryLabel}`" @click="category = 'all'">×</button></span>
        </div>
      </div>

      <p v-if="filtersActive && loaded" class="rv-note">{{ filtered.length }} z {{ rows.length }} · miejsca ogólne</p>

      <div v-if="!loaded" class="rv-empty">Wczytywanie wyników…</div>
      <div v-else-if="!rows.length" class="rv-empty">Brak zawodników na liście.</div>
      <div v-else-if="!items.length" class="rv-empty">Nikt nie pasuje do wyszukiwania.</div>

      <!-- komputer: tabela -->
      <div v-else-if="!isPhone" class="rv-card">
        <table class="rv-table">
          <caption class="visually-hidden">Wyniki: {{ event?.name || alias }}</caption>
          <thead>
            <tr>
              <th scope="col" class="c-place">Miejsce</th>
              <th scope="col" class="c-bib">Nr</th>
              <th scope="col">Zawodnik</th>
              <th v-if="catOptions.length" scope="col" class="c-cat">Kat.</th>
              <th v-if="hasGender" scope="col" class="c-gender">K / M</th>
              <th v-if="hasPoints" scope="col" class="c-num">Punkty</th>
              <th scope="col" class="c-num">Start</th>
              <th scope="col" class="c-num">Meta</th>
              <th scope="col" class="c-num">Czas</th>
              <th scope="col" class="c-num">Strata</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="item in visibleItems" :key="item.key">
              <tr v-if="item.section" class="rv-section">
                <th :colspan="7 + (catOptions.length ? 1 : 0) + (hasGender ? 1 : 0) + (hasPoints ? 1 : 0)" scope="colgroup">{{ item.section }}</th>
              </tr>
              <tr v-else :class="{ 'is-changed': changedIds.has(item.row.id) }">
                <td class="c-place">
                  <span v-if="item.row.place" class="rv-medal" :class="`rv-medal--${item.row.place}`" :aria-label="`${item.row.place}. miejsce`">{{ item.row.place }}</span>
                  <span v-else-if="item.row.group === 'dnf'" class="rv-tag rv-tag--dnf">DNF</span>
                  <span v-else-if="item.row.group === 'dns'" class="rv-tag">DNS</span>
                  <span v-else-if="item.row.outOfCompetition" class="rv-tag" title="Poza klasyfikacją">poza kl.</span>
                  <span v-else class="rv-dash">–</span>
                </td>
                <td class="c-bib"><span class="rv-bib">{{ item.row.number }}</span></td>
                <td class="c-name">
                  <span class="rv-name">{{ item.row.name }}</span>
                  <span v-if="item.row.bio" class="rv-bio">{{ item.row.bio }}</span>
                </td>
                <td v-if="catOptions.length" class="c-cat">{{ item.row.categoryName }}<strong v-if="item.row.categoryPlace">{{ ' ' + item.row.categoryPlace + '.' }}</strong></td>
                <td v-if="hasGender" class="c-gender">
                  <template v-if="item.row.gender"><span :class="`rv-g rv-g--${item.row.gender}`">{{ item.row.gender }}</span><strong v-if="item.row.genderPlace">{{ ' ' + item.row.genderPlace + '.' }}</strong></template>
                </td>
                <td v-if="hasPoints" class="c-num"><strong>{{ item.row.points }}</strong></td>
                <td class="c-num c-clock">{{ item.row.startClock }}</td>
                <td class="c-num c-clock">{{ item.row.finishClock }}<span v-if="item.row.gpsOnly" class="rv-gps" title="Czas wstępny z GPS">GPS</span></td>
                <td class="c-num c-time">
                  <template v-if="item.row.group === 'finished'">
                    <span class="rv-time">{{ item.row.timeText }}</span>
                    <span v-if="item.row.penaltyMin" class="rv-penalty">w tym kara +{{ item.row.penaltyMin }} min</span>
                  </template>
                  <template v-else-if="item.row.group === 'course'">
                    <!-- przed własnym startem nic: godzina startu jest w kolumnie „Start” -->
                    <span v-if="phase === 'ended'" class="rv-muted">brak mety</span>
                    <RunningTime v-else-if="phase === 'live' && !(item.row.startMs != null && item.row.startMs > nowAtBuild)" :start-ms="item.row.startMs" />
                    <span v-if="item.row.lastPoint && phase !== 'before'" class="rv-sub">ostatnio: {{ item.row.lastPoint }}</span>
                  </template>
                  <span v-else-if="item.row.group === 'dnf' && item.row.lastPoint" class="rv-sub">zszedł po: {{ item.row.lastPoint }}</span>
                </td>
                <td class="c-num c-gap">{{ item.row.gapText }}<span v-if="item.row.pointsGapText" class="rv-sub">{{ item.row.pointsGapText }}</span></td>
              </tr>
            </template>
          </tbody>
        </table>
        <p v-if="visibleItems.length < items.length" class="rv-more">Wczytywanie kolejnych wierszy…</p>
        <button v-if="groups.dns.length && !dnsVisible" type="button" class="rv-dns" @click="showDns = true">
          Pokaż {{ groups.dns.length }} {{ plural(groups.dns.length, 'osobę, która nie wystartowała', 'osoby, które nie wystartowały', 'osób, które nie wystartowały') }} (DNS)
        </button>
      </div>

      <!-- telefon: lista -->
      <ol v-else class="rv-plist" aria-label="Wyniki">
        <template v-for="item in visibleItems" :key="item.key">
          <li v-if="item.section" class="rv-plist__section" role="presentation"><h2>{{ item.section }}</h2></li>
          <li v-else class="rv-prow" :class="{ 'is-changed': changedIds.has(item.row.id) }">
            <span class="rv-prow__place">
              <span v-if="item.row.place" class="rv-medal" :class="`rv-medal--${item.row.place}`" :aria-label="`${item.row.place}. miejsce`">{{ item.row.place }}</span>
              <span v-else-if="item.row.group === 'dnf'" class="rv-tag rv-tag--dnf">DNF</span>
              <span v-else-if="item.row.group === 'dns'" class="rv-tag">DNS</span>
              <span v-else class="rv-dash">–</span>
            </span>
            <span class="rv-prow__main">
              <span class="rv-prow__name">{{ item.row.name }}</span>
              <span class="rv-prow__sub">
                {{ item.row.number }}<template v-if="item.row.categoryName"> · {{ item.row.categoryName }}<template v-if="item.row.categoryPlace">{{ ' ' + item.row.categoryPlace + '.' }}</template></template><template v-if="item.row.gender && item.row.genderPlace"> · <span :class="`rv-g rv-g--${item.row.gender}`">{{ item.row.gender }}</span> {{ item.row.genderPlace }}.</template><template v-if="item.row.outOfCompetition"> · poza klasyfikacją</template><template v-if="item.row.gpsOnly"> · <span class="rv-gps">GPS</span></template><template v-if="item.row.group !== 'finished' && item.row.lastPoint && phase !== 'before'"> · {{ item.row.group === 'dnf' ? 'zszedł po' : 'ostatnio' }} {{ item.row.lastPoint }}</template>
              </span>
              <span v-if="item.row.bio" class="rv-prow__bio">{{ item.row.bio }}</span>
            </span>
            <span class="rv-prow__right">
              <template v-if="item.row.group === 'finished'">
                <span class="rv-time">{{ hasPoints ? `${item.row.points} pkt` : item.row.timeText }}</span>
                <span class="rv-sub">{{ hasPoints ? item.row.timeText : (item.row.gapText || '\u00a0') }}</span>
                <span v-if="item.row.penaltyMin" class="rv-penalty">kara +{{ item.row.penaltyMin }} min</span>
              </template>
              <template v-else-if="item.row.group === 'course'">
                <span v-if="phase === 'ended'" class="rv-muted">brak mety</span>
                <span v-else-if="item.row.startMs != null && item.row.startMs > nowAtBuild" class="rv-muted">start {{ item.row.startClock.slice(0, 5) }}</span>
                <template v-else-if="phase === 'live'"><RunningTime :start-ms="item.row.startMs" /><span class="rv-sub">w trasie</span></template>
              </template>
            </span>
          </li>
        </template>
        <li v-if="visibleItems.length < items.length" class="rv-more" role="presentation">Wczytywanie kolejnych wierszy…</li>
        <li v-if="groups.dns.length && !dnsVisible" role="presentation">
          <button type="button" class="rv-dns" @click="showDns = true">
            Pokaż {{ groups.dns.length }} {{ plural(groups.dns.length, 'osobę, która nie wystartowała', 'osoby, które nie wystartowały', 'osób, które nie wystartowały') }}
          </button>
        </li>
      </ol>
    </main>

    <PartnersFooter :sponsors="sponsors" :updated-at="phase === 'live' ? updatedAtText : ''" />

    <!-- telefon: arkusz „Filtry” (W5) -->
    <div v-if="sheetOpen" class="rv-sheet" @click.self="sheetOpen = false">
      <div class="rv-sheet__panel" role="dialog" aria-modal="true" aria-labelledby="rv-sheet-title">
        <div class="rv-sheet__head">
          <h2 id="rv-sheet-title">Filtry</h2>
          <button type="button" class="rv-link" @click="clearFilters">Wyczyść</button>
        </div>
        <template v-if="hasGender">
          <h3>Płeć</h3>
          <div class="rv-seg rv-seg--wide" role="group" aria-label="Płeć">
            <button v-for="opt in [['all', 'Wszyscy'], ['K', 'Kobiety'], ['M', 'Mężczyźni']]" :key="opt[0]" type="button" :aria-pressed="gender === opt[0] ? 'true' : 'false'" @click="gender = opt[0]">{{ opt[1] }} · {{ genderCounts[opt[0]] }}</button>
          </div>
        </template>
        <template v-if="catOptions.length > 1">
          <h3>Kategoria</h3>
          <div class="rv-sheet__chips">
            <button type="button" class="rv-pick" :aria-pressed="category === 'all' ? 'true' : 'false'" @click="category = 'all'">Wszystkie</button>
            <button v-for="c in catOptions" :key="c.id" type="button" class="rv-pick" :aria-pressed="category === c.id ? 'true' : 'false'" @click="category = c.id">{{ c.name }} · {{ c.count }}</button>
          </div>
        </template>
        <button type="button" class="rv-sheet__go" @click="sheetOpen = false">Pokaż {{ filtered.length }} {{ plural(filtered.length, 'osobę', 'osoby', 'osób') }}</button>
      </div>
    </div>
  </div>
</template>

<style>
/* App.vue ustawia #app na wyśrodkowany tekst z marginesem — ta strona ma własny układ */
html.rv-page #app {
  margin-top: 0;
  text-align: left;
  color: var(--sledz-text);
}
html.rv-page body {
  background: var(--sledz-surface-alt);
}
</style>

<style scoped>
.rv {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background: var(--sledz-surface-alt);
  color: var(--sledz-text);
}
.rv__main {
  flex: 1;
  width: 100%;
  max-width: 1120px;
  margin: 0 auto;
  padding: 20px 24px 28px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ---------- toolbar ---------- */
.rv-toolbar {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}
.rv-toolbar__spacer {
  flex: 1;
}
.rv-toolbar__summary {
  font-size: 14px;
  color: var(--sledz-text-muted);
}
.rv-search {
  flex: none;
  width: 300px;
  height: 40px;
  margin: 0;
  padding: 0 12px;
  border: 1px solid var(--sledz-border);
  border-radius: 10px;
  background: var(--sledz-surface);
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--sledz-text-muted);
}
.rv-search:focus-within {
  border-color: var(--sledz-primary);
  box-shadow: 0 0 0 3px var(--sledz-primary-soft);
}
.rv-search input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-size: 15px;
  color: var(--sledz-text);
}
.rv-seg {
  display: inline-flex;
  padding: 3px;
  border-radius: 20px;
  background: #eef2f7;
}
.rv-seg button {
  height: 34px;
  padding: 0 14px;
  border: none;
  border-radius: 17px;
  background: transparent;
  color: var(--sledz-text-secondary);
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}
.rv-seg button[aria-pressed='true'] {
  background: var(--sledz-surface);
  color: var(--sledz-text);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
}
.rv-select {
  height: 40px;
  margin: 0;
  padding: 0 4px 0 12px;
  border: 1px solid var(--sledz-border);
  border-radius: 10px;
  background: var(--sledz-surface);
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 14px;
  color: var(--sledz-text-muted);
}
.rv-select.is-active {
  border: 1.5px solid var(--sledz-primary);
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
}
.rv-select select {
  height: 36px;
  border: none;
  background: transparent;
  font-size: 14px;
  font-weight: 700;
  color: var(--sledz-text);
  cursor: pointer;
}
.rv-note {
  margin: 0;
  font-size: 13.5px;
  color: var(--sledz-text-muted);
}
.rv-empty {
  padding: 40px 16px;
  text-align: center;
  color: var(--sledz-text-muted);
}

/* ---------- desktop table ---------- */
.rv-card {
  background: var(--sledz-surface);
  border: 1px solid var(--sledz-border-soft);
  border-radius: 14px;
  overflow: hidden;
  overflow: clip; /* nie tworzy kontenera przewijania — nagłówek tabeli zostaje przyklejony */
}
.rv-table {
  width: 100%;
  border-collapse: collapse;
}
.rv-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  height: 44px;
  padding: 0 12px;
  background: var(--sledz-surface-alt);
  border-bottom: 1px solid var(--sledz-border-soft);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
  text-align: left;
  white-space: nowrap;
}
.rv-table td {
  height: 52px;
  padding: 6px 12px;
  border-bottom: 1px solid #eef2f7;
  vertical-align: middle;
  font-size: 14px;
}
.rv-table tbody tr.is-changed td {
  animation: rv-flash 1.2s ease-out;
  background: var(--sledz-highlight);
}
@keyframes rv-flash {
  0% { background: var(--sledz-highlight-flash); }
  100% { background: var(--sledz-highlight); }
}
.rv-table .c-place {
  width: 80px;
  text-align: center;
}
.rv-table .c-bib {
  width: 70px;
}
.rv-table .c-cat {
  width: 120px;
  white-space: nowrap;
  color: var(--sledz-text-secondary);
}
.rv-table .c-gender {
  width: 80px;
  white-space: nowrap;
  color: var(--sledz-text-secondary);
}
.rv-table .c-num {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.rv-table .c-clock {
  width: 90px;
  font-size: 13.5px;
  color: var(--sledz-text-muted);
}
.rv-table .c-time {
  width: 120px;
}
.rv-table .c-gap {
  width: 90px;
  color: var(--sledz-text-muted);
}
.rv-section th {
  padding: 18px 16px 8px;
  border-bottom: 1px solid var(--sledz-border-soft);
  background: var(--sledz-surface);
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
  text-align: left;
}
.rv-name {
  display: block;
  font-size: 15px;
  font-weight: 700;
  color: var(--sledz-text);
}
.rv-bio {
  display: block;
  margin-top: 1px;
  max-width: 420px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 12.5px;
  color: var(--sledz-text-muted);
}
.rv-time {
  display: block;
  font-size: 16px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--sledz-text);
}
.rv-sub {
  display: block;
  font-size: 12.5px;
  color: var(--sledz-text-muted);
  white-space: nowrap;
}
.rv-muted {
  color: var(--sledz-text-muted);
  font-size: 13.5px;
}
.rv-penalty {
  display: block;
  font-size: 12px;
  font-weight: 700;
  color: var(--sledz-warning-ink);
  white-space: nowrap;
}
.rv-medal {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 30px;
  height: 30px;
  padding: 0 4px;
  border-radius: 15px;
  font-size: 15px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
  color: var(--sledz-text);
}
.rv-medal--1,
.rv-medal--2,
.rv-medal--3 {
  font-size: 14px;
  font-weight: 900;
  color: var(--sledz-brand-ink);
  box-shadow: inset 0 -2px 0 rgba(0, 0, 0, 0.12);
}
.rv-medal--1 {
  background: var(--sledz-brand);
}
.rv-medal--2 {
  background: var(--sledz-medal-silver);
}
.rv-medal--3 {
  background: var(--sledz-medal-bronze);
}
.rv-tag {
  display: inline-block;
  padding: 2px 7px;
  border-radius: 6px;
  background: #eef2f7;
  color: var(--sledz-text-secondary);
  font-size: 11.5px;
  font-weight: 800;
  white-space: nowrap;
}
.rv-tag--dnf {
  background: #fee2e2;
  color: var(--sledz-live-strong);
}
.rv-dash {
  color: var(--sledz-text-muted);
}
.rv-bib {
  display: inline-block;
  min-width: 38px;
  padding: 2px 6px;
  border-radius: 6px;
  background: #eef2f7;
  color: var(--sledz-text-secondary);
  text-align: center;
  font-size: 12.5px;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}
.rv-g {
  font-weight: 800;
}
.rv-g--K {
  color: var(--sledz-gender-k);
}
.rv-g--M {
  color: var(--sledz-gender-m);
}
.rv-gps {
  margin-left: 4px;
  padding: 0 4px;
  border-radius: 4px;
  background: var(--sledz-warning-soft);
  color: var(--sledz-warning-ink);
  font-size: 10.5px;
  font-weight: 800;
}
.rv-more {
  margin: 0;
  padding: 12px 16px;
  text-align: center;
  font-size: 13.5px;
  color: var(--sledz-text-muted);
  list-style: none;
}
.rv-dns {
  display: block;
  width: 100%;
  padding: 12px 16px;
  border: none;
  background: transparent;
  text-align: left;
  font-size: 14px;
  font-weight: 700;
  color: var(--sledz-primary-deep);
}

/* ---------- phone ---------- */
.rv-ptools {
  position: sticky;
  top: 0;
  z-index: 5;
  margin: -20px -16px 0;
  padding: 10px 16px;
  background: var(--sledz-surface-alt);
  border-bottom: 1px solid var(--sledz-border-soft);
}
.rv-ptools__row {
  display: flex;
  gap: 8px;
}
.rv-search--phone {
  flex: 1;
  width: auto;
  min-width: 0;
  height: 44px;
}
.rv-search--phone input {
  font-size: 16px; /* mniejsza czcionka = iOS przybliża stronę */
}
.rv-filters-btn {
  flex: none;
  height: 44px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid var(--sledz-border);
  background: var(--sledz-surface);
  color: var(--sledz-text);
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14.5px;
  font-weight: 700;
}
.rv-filters-btn.is-active {
  border-color: var(--sledz-primary);
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
}
.rv-filters-btn__badge {
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: var(--sledz-primary);
  color: #ffffff;
  font-size: 11.5px;
  line-height: 18px;
  text-align: center;
}
.rv-chips {
  display: flex;
  gap: 6px;
  margin-top: 8px;
  flex-wrap: wrap;
}
.rv-chip {
  height: 32px;
  padding: 0 4px 0 12px;
  border-radius: 16px;
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
  display: inline-flex;
  align-items: center;
  gap: 2px;
  font-size: 13.5px;
  font-weight: 700;
}
.rv-chip button {
  width: 28px;
  height: 28px;
  border: none;
  background: transparent;
  color: var(--sledz-primary-deep);
  font-size: 18px;
  line-height: 1;
}
.rv-plist {
  margin: 0 -16px;
  padding: 0;
  list-style: none;
  background: var(--sledz-surface);
}
.rv-plist__section h2 {
  margin: 0;
  padding: 16px 16px 6px;
  background: var(--sledz-surface-alt);
  border-bottom: 1px solid var(--sledz-border-soft);
  font-size: 12px;
  font-weight: 800;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
}
.rv-prow {
  min-height: 64px;
  padding: 8px 16px;
  border-bottom: 1px solid #eef2f7;
  display: flex;
  align-items: center;
  gap: 12px;
  content-visibility: auto;
  contain-intrinsic-size: auto 64px;
}
.rv-prow.is-changed {
  animation: rv-flash 1.2s ease-out;
  background: var(--sledz-highlight);
}
.rv-prow__place {
  flex: none;
  width: 32px;
  display: flex;
  justify-content: center;
}
.rv-prow__main {
  flex: 1;
  min-width: 0;
}
.rv-prow__name {
  display: block;
  font-size: 15.5px;
  font-weight: 700;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rv-prow__sub,
.rv-prow__bio {
  display: block;
  margin-top: 2px;
  font-size: 13px;
  color: var(--sledz-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.rv-prow__right {
  flex: none;
  text-align: right;
  font-variant-numeric: tabular-nums;
}
.rv-prow__right .rv-sub {
  text-align: right;
}

/* ---------- filter sheet ---------- */
.rv-sheet {
  position: fixed;
  inset: 0;
  z-index: 50;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: flex-end;
}
.rv-sheet__panel {
  width: 100%;
  max-height: 85vh;
  overflow-y: auto;
  padding: 20px 16px calc(20px + env(safe-area-inset-bottom));
  border-radius: 18px 18px 0 0;
  background: var(--sledz-surface);
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.2);
}
.rv-sheet__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
}
.rv-sheet__head h2 {
  margin: 0;
  font-size: 19px;
  font-weight: 800;
}
.rv-sheet h3 {
  margin: 18px 0 8px;
  font-size: 12.5px;
  font-weight: 800;
  letter-spacing: 0.4px;
  text-transform: uppercase;
  color: var(--sledz-text-muted);
}
.rv-seg--wide {
  display: flex;
}
.rv-seg--wide button {
  flex: 1;
  height: 38px;
  padding: 0 8px;
}
.rv-sheet__chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.rv-pick {
  height: 38px;
  padding: 0 14px;
  border-radius: 19px;
  border: 1px solid var(--sledz-border);
  background: var(--sledz-surface);
  color: var(--sledz-text);
  font-size: 14px;
  font-weight: 700;
  white-space: nowrap;
}
.rv-pick[aria-pressed='true'] {
  border: 1.5px solid var(--sledz-primary);
  background: var(--sledz-primary-soft);
  color: var(--sledz-primary-deep);
}
.rv-link {
  border: none;
  background: transparent;
  padding: 0;
  font-size: 14px;
  font-weight: 700;
  color: var(--sledz-primary-deep);
}
.rv-sheet__go {
  margin-top: 22px;
  width: 100%;
  height: 50px;
  border: none;
  border-radius: 12px;
  background: var(--sledz-primary);
  color: #ffffff;
  font-size: 16px;
  font-weight: 800;
}

@media (max-width: 767.98px) {
  .rv__main {
    padding: 20px 16px 0;
    gap: 0;
  }
  .rv-note {
    margin: 0 -16px;
    padding: 8px 16px;
    background: var(--sledz-surface);
    border-bottom: 1px solid var(--sledz-border-soft);
  }
}
</style>
